import {
    getScrollableElementPosition,
    isScrollableElement,
    preventElementScroll,
    ScrollPosition,
} from "../../utils";
import type { Popup } from "./Popup";
import { PopupType } from "./popup-config";

const CLOSING_DISTANCE_THRESHOLD_PX = 80;

export function canStartClosingSwipe(target: HTMLElement, contentElement: HTMLElement | null): boolean {
    if (contentElement === null || !contentElement.contains(target)) {
        return true;
    }

    const scrollableElement = findClosestScrollableElement(target, contentElement);

    if (scrollableElement === null) {
        return true;
    }

    return scrollableElement === contentElement
        && getScrollableElementPosition(scrollableElement) === ScrollPosition.Top;
}

function findClosestScrollableElement(startElement: HTMLElement, boundaryElement: HTMLElement): HTMLElement | null {
    for (let element: HTMLElement | null = startElement; element !== null; element = element.parentElement) {
        if (isScrollableElement(element)) {
            return element;
        }

        if (element === boundaryElement) {
            return null;
        }
    }

    return null;
}

export class SheetSwipe {
    private readonly gestureAbortController = new AbortController();
    private isDragging = false;
    private currentOffset = 0;

    static startIfPossible(popup: Popup, event: PointerEvent, onFinish: (isClosed: boolean) => void): void {
        if (popup.type !== PopupType.SHEET || !popup.isOpen) {
            return;
        }

        if (!(event.target instanceof HTMLElement) || !canStartClosingSwipe(event.target, popup.content)) {
            return;
        }

        new SheetSwipe(popup, event.clientY, onFinish);
    }

    private constructor(
        private readonly popup: Popup,
        private readonly startClientY: number,
        private readonly onFinish: (isClosed: boolean) => void,
    ) {
        const { signal } = this.gestureAbortController;

        document.addEventListener("pointermove", this.handlePointerMove, { signal });
        document.addEventListener("pointerup", () => this.finish(true), { signal });
        document.addEventListener("pointercancel", () => this.finish(false), { signal });
    }

    private readonly handlePointerMove = (event: PointerEvent): void => {
        this.currentOffset = event.clientY - this.startClientY;

        if (this.currentOffset > 0 && !this.isDragging) {
            this.startDragging();
        }

        if (this.isDragging) {
            this.popup.element.style.transform = `translateY(${Math.max(0, this.currentOffset)}px)`;
        }
    };

    private startDragging(): void {
        this.isDragging = true;
        this.popup.setTransitionsEnabled(false);

        this.popup.content?.addEventListener("touchmove", preventElementScroll, {
            signal: this.gestureAbortController.signal,
        });
    }

    private finish(isReleased: boolean): void {
        this.gestureAbortController.abort();

        const isClosed = isReleased && this.currentOffset >= CLOSING_DISTANCE_THRESHOLD_PX;

        this.popup.setTransitionsEnabled(true);

        if (isClosed) {
            this.popup.dismiss();
        }

        this.popup.element.style.transform = "";

        this.onFinish(isClosed);
    }
}
